import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Icon from '../components/Icon';
import { useT } from '../i18n/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const Unsubscribe: React.FC = () => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [senderDomain, setSenderDomain] = useState('');

  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const campaignId = searchParams.get('campaignId');
  const fromEmail = searchParams.get('from') || ''; // Optional: sender email

  useEffect(() => {
    // Extract sender domain from fromEmail or use default
    if (fromEmail) {
      const domain = fromEmail.split('@')[1];
      setSenderDomain(domain);
    } else {
      setSenderDomain('bebakids.com'); // Default domain
    }
    handleUnsubscribe();
  }, []);

  const handleUnsubscribe = async () => {
    if (!email || !token) {
      setStatus('error');
      setMessage(t('Nevažeći link. Email i token su obavezni.', 'Invalid link. Email and token are required.'));
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/unsubscribe`, {
        params: { email, token, campaignId },
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(t('Uspešno ste se odjavili sa svih budućih email poruka.', 'You have successfully unsubscribed from all future emails.'));
      } else {
        setStatus('error');
        setMessage(response.data.error || t('Došlo je do greške.', 'An error occurred.'));
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.response?.data?.error || t('Došlo je do greške prilikom odjave.', 'An error occurred while unsubscribing.'));
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg)',
        padding: 24,
      }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 440, overflow: 'hidden' }}>
        {/* Brand mark + title */}
        <div
          className="card-pad"
          style={{ textAlign: 'center', borderBottom: '1px solid var(--border)' }}
        >
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: 14 }}>
            <span className="brand-mark" style={{ width: 44, height: 44, borderRadius: 12 }}>
              <Icon name="mail" size={22} />
            </span>
          </div>
          <h1 className="section-title">{t('Odjava sa Email Lista', 'Unsubscribe from Email Lists')}</h1>
          {senderDomain && (
            <p className="muted" style={{ fontSize: 14, marginTop: 5 }}>
              Webshop - {senderDomain}
            </p>
          )}
        </div>

        <div className="card-pad" style={{ textAlign: 'center', padding: '36px 28px' }}>
          {status === 'loading' && (
            <>
              <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
              <p className="muted">{t('Obrada vašeg zahteva...', 'Processing your request...')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ color: 'var(--ok)', marginBottom: 20 }}>
                <Icon name="checkCircle" size={64} />
              </div>
              <h2 style={{ color: 'var(--brand-700)', marginBottom: 15 }}>{t('Uspešno Odjavljeni!', 'Successfully Unsubscribed!')}</h2>
              <p style={{ color: 'var(--text-2)', fontSize: 16, lineHeight: 1.8, marginBottom: 25 }}>
                {t('Vaša email adresa', 'Your email address')} <strong>{email}</strong> {t('je uspešno uklonjena sa naše mailing liste.', 'has been successfully removed from our mailing list.')}
              </p>

              <div
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  padding: 20,
                  borderRadius: 8,
                  marginTop: 25,
                  textAlign: 'left',
                }}
              >
                <h3 style={{ fontSize: 16, color: 'var(--text)', marginBottom: 15 }}>
                  {t('Šta ovo znači?', 'What does this mean?')}
                </h3>
                <ul
                  style={{
                    color: 'var(--text-2)',
                    fontSize: 14,
                    lineHeight: 1.8,
                    paddingLeft: 20,
                  }}
                >
                  <li>{t('Više nećete primati marketing emailove od', 'You will no longer receive marketing emails from')} {senderDomain}</li>
                  <li>{t('Odjava stupa na snagu odmah', 'Unsubscription takes effect immediately')}</li>
                  <li>{t('I dalje ćete primati transakcione emailove (potvrde narudžbina, računi, itd.)', 'You will still receive transactional emails (order confirmations, invoices, etc.)')}</li>
                  <li>{t('Možete se ponovo prijaviti u bilo kom trenutku', 'You can re-subscribe at any time')}</li>
                </ul>
              </div>

              <p
                style={{
                  color: 'var(--text-3)',
                  fontSize: 13,
                  marginTop: 30,
                  fontStyle: 'italic',
                }}
              >
                {t('Ako ste se odjavili greškom, molimo vas kontaktirajte našu korisničku podršku na', 'If you unsubscribed by mistake, please contact our customer support at')}{' '}
                <a href={`https://${senderDomain}`} style={{ color: 'var(--brand-600)' }}>
                  {senderDomain}
                </a>
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ color: 'var(--danger)', marginBottom: 20 }}>
                <Icon name="x" size={64} />
              </div>
              <h2 style={{ color: 'var(--danger-strong)', marginBottom: 15 }}>{t('Došlo je do Greške', 'An Error Occurred')}</h2>
              <p style={{ color: 'var(--text-2)', fontSize: 16, lineHeight: 1.6, marginBottom: 20 }}>
                {message}
              </p>

              <div
                style={{
                  background: 'var(--danger-soft)',
                  border: '1px solid var(--danger)',
                  padding: 20,
                  borderRadius: 8,
                  marginTop: 25,
                  textAlign: 'left',
                }}
              >
                <h3 style={{ fontSize: 16, color: 'var(--danger-strong)', marginBottom: 15 }}>
                  {t('Mogući razlozi:', 'Possible reasons:')}
                </h3>
                <ul
                  style={{
                    color: 'var(--text-2)',
                    fontSize: 14,
                    lineHeight: 1.8,
                    paddingLeft: 20,
                  }}
                >
                  <li>{t('Link je istekao ili je neispravan', 'The link has expired or is invalid')}</li>
                  <li>{t('Već ste odjavljeni sa ove liste', 'You are already unsubscribed from this list')}</li>
                  <li>{t('Email adresa nije pronađena u našem sistemu', 'The email address was not found in our system')}</li>
                </ul>
              </div>

              <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 30 }}>
                {t('Za dodatnu pomoć, molimo vas kontaktirajte:', 'For further assistance, please contact:')}{' '}
                <a href={`https://${senderDomain}/podrska`} style={{ color: 'var(--brand-600)' }}>
                  {t('Korisnička Podrška', 'Customer Support')}
                </a>
              </p>
            </>
          )}
        </div>

        <div
          className="card-pad"
          style={{ borderTop: '1px solid var(--border)', textAlign: 'center' }}
        >
          <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 8 }}>
            © {new Date().getFullYear()} {senderDomain ? `Webshop - ${senderDomain}` : 'Webshop'}. {t('Sva prava zadržana.', 'All rights reserved.')}
          </p>
          <p style={{ color: 'var(--text-3)', fontSize: 12 }}>
            {t('Ova stranica je deo našeg sistema za upravljanje email marketingom.', 'This page is part of our email marketing management system.')}
            <br />
            {t('Vaša privatnost je naš prioritet. Više informacija na', 'Your privacy is our priority. More information in the')}{' '}
            <a href={`https://${senderDomain}/privatnost`} style={{ color: 'var(--brand-600)' }}>
              {t('politici privatnosti', 'privacy policy')}
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
